import { API_URL } from "../config/env";
import {
  parseJson,
  type AtHomeSupportResponse,
  type ActionCardDocumentMutationResponse,
  type ActionCardDocumentsResponse,
  type ActionCardFlagMutationResponse,
  type ActionCardFlagsResponse,
  type AdminRequestsResponse,
  type AdminSchoolThreadsResponse,
  type AdminStaffContactsResponse,
  type AdminStaffMessageResponse,
  type AdminStaffThreadResponse,
  type AdminStudentsResponse,
  type AccountAccessResponse,
  type AccountEmailResponse,
  type AccountHistoryResponse,
  type ApiSuccessMessage,
  type AttendanceProgressionSummaryResponse,
  type AuthLoginResponse,
  type BreakoutMutationResponse,
  type BreakoutRoomResponse,
  type BreakoutRoomsResponse,
  type ChangePasswordResponse,
  type ClassDataPointResponse,
  type ForgotPasswordResponse,
  type GradesResponse,
  type ApiData,
  type LoginResult,
  type ManagedAccountsResponse,
  type MarkMessagesReadResponse,
  type MessagingSummaryResponse,
  type ParentInvitationRecipientsResponse,
  type ParentInvitationsSendResponse,
  type ParentRightsRequestMutationResponse,
  type ParentRightsRequestsResponse,
  type ParentStudentsResponse,
  type ProgressChatContactsResponse,
  type ProgressChatMessagesResponse,
  type ProgressionGridResponse,
  type ResetPasswordResponse,
  type RightsRequestsResponse,
  type RosterUploadResponse,
  type RotateBootstrapPasswordResponse,
  type SchoolDetailResponse,
  type SchoolInviteResponse,
  type SchoolListResponse,
  type SendProgressChatMessageResponse,
  type StandardsScopeResponse,
  type StudentSubjectsResponse,
  type SuccessSkillsResponse,
  type SupportBankGradesResponse,
  type SupportBankResourcesResponse,
  type SupportBankSubjectsResponse,
  type TeacherClassDetailResponse,
  type TeacherParentsResponse,
  type TeacherStudentDetailResponse,
  type TeacherStudentsResponse,
  type TeacherSubjectsResponse,
  type TileColorsResponse,
  type TwoFactorSetupResponse,
  type TwoFactorStatusResponse,
  type TwoFactorToggleResponse,
} from "../types/api";

export { API_URL };

// ── Breakout Room API ─────────────────────────────────────────────
export async function fetchBreakoutRooms(classId: any): Promise<BreakoutRoomsResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/by_class/${classId}`);
  if (!res.ok) throw new Error('Failed to fetch breakout rooms');
  return parseJson<BreakoutRoomsResponse>(res);
}

export async function getSchoolStandardsScope(schoolId: any): Promise<StandardsScopeResponse> {
  const res = await fetch(`${API_URL}/api/standards-scope/schools?school_id=${encodeURIComponent(schoolId)}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load school standards preferences');
  return parseJson<StandardsScopeResponse>(res);
}

export async function saveSchoolStandardsScope({ schoolId, subject, standardSource, gradeBand, timeFrameType, timeFrameLabel, mlsCodes }: any): Promise<StandardsScopeResponse> {
  const res = await fetch(`${API_URL}/api/standards-scope/schools/${encodeURIComponent(schoolId)}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject,
      standard_source: standardSource,
      grade_band: gradeBand,
      time_frame_type: timeFrameType,
      time_frame_label: timeFrameLabel,
      mls_codes: mlsCodes,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save school standards preference');
  }
  return parseJson<StandardsScopeResponse>(res);
}

export async function saveSchoolScienceSource(schoolId: any, standardSource: any): Promise<StandardsScopeResponse> {
  const res = await fetch(`${API_URL}/api/standards-scope/schools/${encodeURIComponent(schoolId)}/science-source`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ standard_source: standardSource }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save school Science standards preference');
  }
  return parseJson<StandardsScopeResponse>(res);
}

export async function createBreakoutRoom(room: any, classId: any): Promise<BreakoutRoomResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/?class_id=${classId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(room),
  });
  if (!res.ok) throw new Error('Failed to create breakout room');
  return parseJson<BreakoutRoomResponse>(res);
}

export async function updateBreakoutRoom(breakoutRoomId: any, room: any): Promise<BreakoutRoomResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/${breakoutRoomId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(room),
  });
  if (!res.ok) throw new Error('Failed to update breakout room');
  return parseJson<BreakoutRoomResponse>(res);
}

export async function addStudentToBreakoutRoom(breakoutRoomId: any, student: any): Promise<BreakoutMutationResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/${breakoutRoomId}/students`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(student),
  });
  if (!res.ok) throw new Error('Failed to add student to breakout room');
  return parseJson<BreakoutMutationResponse>(res);
}

export async function removeStudentFromBreakoutRoom(breakoutRoomId: any, studentId: any): Promise<BreakoutMutationResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/${breakoutRoomId}/students/${studentId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to remove student from breakout room');
  return parseJson<BreakoutMutationResponse>(res);
}

export async function deleteBreakoutRoom(breakoutRoomId: any): Promise<BreakoutMutationResponse> {
  const res = await authFetch(`${API_URL}/api/breakout_rooms/${breakoutRoomId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete breakout room');
  return parseJson<BreakoutMutationResponse>(res);
}
// Fetch full progression grid for a student (PK-12, all subjects), optionally filtered by class
export async function fetchStudentProgressionGrid(studentId: any, classId: any): Promise<ProgressionGridResponse> {
  const url = classId
    ? `${API_URL}/api/data-points/progression-grid/${studentId}/${classId}`
    : `${API_URL}/api/data-points/progression-grid/${studentId}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error('Failed to fetch progression grid');
  return parseJson<ProgressionGridResponse>(res);
}

export async function fetchStudentProgressWorkbook(studentId: any, classId: any): Promise<Blob> {
  const url = classId
    ? `${API_URL}/api/data-points/progression-export/${studentId}/${classId}`
    : `${API_URL}/api/data-points/progression-export/${studentId}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error('Failed to export progression workbook');
  return res.blob();
}

export async function fetchStudentProgressPdf(studentId: any, classId: any, { fromDate, throughDate }: any = {}): Promise<Blob> {
  const url = classId
    ? `${API_URL}/api/data-points/progression-report-pdf/${studentId}/${classId}`
    : `${API_URL}/api/data-points/progression-report-pdf/${studentId}`;
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (throughDate) params.set('through_date', throughDate);
  const query = params.toString();
  const res = await authFetch(query ? `${url}?${query}` : url);
  if (!res.ok) throw new Error('Failed to export progression PDF');
  return res.blob();
}

export async function bulkDownloadFamilyReports(studentIds: any, classId: any, { fromDate, throughDate }: any = {}): Promise<Blob> {
  const res = await authFetch(`${API_URL}/api/data-points/bulk-progression-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      student_ids: studentIds, class_id: classId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(throughDate ? { through_date: throughDate } : {}),
    }),
  });
  if (!res.ok) throw new Error('Failed to generate bulk reports');
  return res.blob();
}

// Support Bank
export async function fetchAtHomeSupport({ grade, subject, strand, standard }: any = {}): Promise<AtHomeSupportResponse> {
  const params = new URLSearchParams();
  if (grade) params.set('grade', grade);
  if (subject) params.set('subject', subject);
  if (strand) params.set('strand', strand);
  if (standard) params.set('standard', standard);
  const res = await authFetch(`${API_URL}/api/support-bank/at-home?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch at-home support');
  return parseJson<AtHomeSupportResponse>(res);
}

export async function fetchSuccessSkills({ grade, section }: any = {}): Promise<SuccessSkillsResponse> {
  const params = new URLSearchParams();
  if (grade) params.set('grade', grade);
  if (section) params.set('section', section);
  const res = await authFetch(`${API_URL}/api/support-bank/success-skills?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch success skills');
  return parseJson<SuccessSkillsResponse>(res);
}

export async function fetchSupportBankGrades(): Promise<SupportBankGradesResponse> {
  const res = await authFetch(`${API_URL}/api/support-bank/grades`);
  if (!res.ok) throw new Error('Failed to fetch grades');
  return parseJson<SupportBankGradesResponse>(res);
}

export async function fetchSupportBankSubjects(grade: any): Promise<SupportBankSubjectsResponse> {
  const params = grade ? `?grade=${encodeURIComponent(grade)}` : '';
  const res = await authFetch(`${API_URL}/api/support-bank/subjects${params}`);
  if (!res.ok) throw new Error('Failed to fetch subjects');
  return parseJson<SupportBankSubjectsResponse>(res);
}

export async function fetchSupportBankResourceFiles(): Promise<SupportBankResourcesResponse> {
  const res = await authFetch(`${API_URL}/api/support-bank/resources`);
  if (!res.ok) throw new Error('Failed to fetch support resources');
  return parseJson<SupportBankResourcesResponse>(res);
}

export async function fetchSupportBankResourceBlob(
  name: any,
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams({ name });
  const res = await authFetch(`${API_URL}/api/support-bank/resources/download?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to download support resource');
  const blob = await res.blob();
  const contentDisposition = res.headers.get('content-disposition') || '';
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] || name;
  return { blob, filename };
}

export async function fetchActionCardDocuments(studentId: any, standardCode: any): Promise<ActionCardDocumentsResponse> {
  const url = `${API_URL}/api/action-card-documents/student/${encodeURIComponent(studentId)}/${encodeURIComponent(standardCode)}`;
  const res = await authFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not load school activity resources.');
  }
  return parseJson<ActionCardDocumentsResponse>(res);
}

export async function fetchMyActionCardDocuments(): Promise<ActionCardDocumentsResponse> {
  const res = await authFetch(`${API_URL}/api/action-card-documents/mine`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not load your school activity resources.');
  }
  return parseJson<ActionCardDocumentsResponse>(res);
}

export async function uploadActionCardDocument(studentId: any, standardCode: any, file: any): Promise<ActionCardDocumentMutationResponse> {
  const url = `${API_URL}/api/action-card-documents/student/${encodeURIComponent(studentId)}/${encodeURIComponent(standardCode)}`;
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(url, { method: 'POST', body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not upload school activity resource.');
  }
  return parseJson<ActionCardDocumentMutationResponse>(res);
}

export async function downloadMyActionCardDocument(documentId: any): Promise<Blob> {
  const res = await authFetch(`${API_URL}/api/action-card-documents/mine/document/${encodeURIComponent(documentId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not download school activity resource.');
  }
  return res.blob();
}

export async function downloadActionCardDocument(studentId: any, documentId: any): Promise<Blob> {
  const url = `${API_URL}/api/action-card-documents/student/${encodeURIComponent(studentId)}/document/${encodeURIComponent(documentId)}`;
  const res = await authFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not download school activity resource.');
  }
  return res.blob();
}

export async function deleteActionCardDocument(studentId: any, documentId: any): Promise<ActionCardDocumentMutationResponse> {
  const url = `${API_URL}/api/action-card-documents/student/${encodeURIComponent(studentId)}/document/${encodeURIComponent(documentId)}`;
  const res = await authFetch(url, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not remove school activity resource.');
  }
  return parseJson<ActionCardDocumentMutationResponse>(res);
}

export async function fetchSchoolActionCardDocuments(standardCode: any): Promise<ActionCardDocumentsResponse> {
  const res = await authFetch(`${API_URL}/api/action-card-documents/school/${encodeURIComponent(standardCode)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not load school activity resources.');
  }
  return parseJson<ActionCardDocumentsResponse>(res);
}

export async function uploadSchoolActionCardDocument(standardCode: any, file: any): Promise<ActionCardDocumentMutationResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(`${API_URL}/api/action-card-documents/school/${encodeURIComponent(standardCode)}`, { method: 'POST', body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not upload school activity resource.');
  }
  return parseJson<ActionCardDocumentMutationResponse>(res);
}

export async function downloadSchoolActionCardDocument(documentId: any): Promise<Blob> {
  const res = await authFetch(`${API_URL}/api/action-card-documents/school/document/${encodeURIComponent(documentId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not download school activity resource.');
  }
  return res.blob();
}

export async function deleteSchoolActionCardDocument(documentId: any): Promise<ActionCardDocumentMutationResponse> {
  const res = await authFetch(`${API_URL}/api/action-card-documents/school/document/${encodeURIComponent(documentId)}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not remove school activity resource.');
  }
  return parseJson<ActionCardDocumentMutationResponse>(res);
}

export async function submitActionCardFlag(studentId: any, payload: any): Promise<ActionCardFlagMutationResponse> {
  const url = `${API_URL}/api/action-card-flags/student/${encodeURIComponent(studentId)}`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Could not send your flag. Please try again.');
  }
  return parseJson<ActionCardFlagMutationResponse>(res);
}

export async function listActionCardFlags(status: any = 'open'): Promise<ActionCardFlagsResponse> {
  const res = await authFetch(`${API_URL}/api/action-card-flags?status=${encodeURIComponent(status)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not load flagged Action Cards.');
  }
  return parseJson<ActionCardFlagsResponse>(res);
}

export async function updateActionCardFlagStatus(flagId: any, status: any): Promise<ActionCardFlagMutationResponse> {
  const res = await authFetch(`${API_URL}/api/action-card-flags/${encodeURIComponent(flagId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not update the flag.');
  }
  return parseJson<ActionCardFlagMutationResponse>(res);
}

// Fetch manually-set tile colors for a student's profile
export async function fetchStudentTileColors(studentId: any): Promise<TileColorsResponse> {
  const res = await authFetch(`${API_URL}/api/profile-tiles/student/${studentId}`);
  if (!res.ok) throw new Error('Failed to fetch tile colors');
  return parseJson<TileColorsResponse>(res); // [{ mls_code, color }]
}

// Save a manually-set tile color for a student
export async function saveStudentTileColor(studentId: any, mlsCode: any, color: any): Promise<TileColorsResponse> {
  const res = await authFetch(`${API_URL}/api/profile-tiles/student/${studentId}/tile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mls_code: mlsCode, color }),
  });
  if (!res.ok) throw new Error('Failed to save tile color');
  return parseJson<TileColorsResponse>(res);
}
// Admin: Update a class-teacher-student data point
export async function updateClassDataPoint(data: any): Promise<ClassDataPointResponse> {
  const res = await authFetch(`${API_URL}/api/admin/class-data-point`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update data point');
  return parseJson<ClassDataPointResponse>(res);
}
// Admin: Create a new class-teacher-student data point
export async function createClassDataPoint(data: any): Promise<ClassDataPointResponse> {
  const res = await authFetch(`${API_URL}/api/admin/class-data-point`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create data point');
  return parseJson<ClassDataPointResponse>(res);
}
// Fetch subjects with classes, students, and teachers for admin
export async function fetchAdminSubjectsWithStudents(): Promise<StudentSubjectsResponse> {
  const res = await authFetch(`${API_URL}/api/admin/subjects-with-students`);
  if (!res.ok) throw new Error('Fetch admin subjects with students failed');
  return parseJson<StudentSubjectsResponse>(res);
}

// Fetch subjects with classes and students for the current student
export async function fetchStudentSubjectsWithStudents(): Promise<StudentSubjectsResponse> {
  const res = await authFetch(`${API_URL}/api/students/subjects`);
  if (!res.ok) throw new Error('Fetch student subjects with students failed');
  return parseJson<StudentSubjectsResponse>(res);
}
// Fetch subjects with classes for the current student
export async function fetchStudentSubjects(): Promise<StudentSubjectsResponse> {
  const res = await fetch(`${API_URL}/api/students/subjects`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch student subjects failed');
  return parseJson<StudentSubjectsResponse>(res);
}
// Fetch subjects with classes and students for the current teacher
export async function fetchTeacherSubjectsWithStudents(): Promise<TeacherSubjectsResponse> {
  const res = await fetch(`${API_URL}/api/teacher/subjects-with-students`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch teacher subjects failed');
  return parseJson<TeacherSubjectsResponse>(res);
}

export async function fetchTeacherAttendanceProgressionSummary(): Promise<AttendanceProgressionSummaryResponse> {
  const res = await authFetch(`${API_URL}/api/v1/attendance/progression-summary/teacher`);
  if (!res.ok) throw new Error('Failed to fetch teacher attendance summary');
  return parseJson<AttendanceProgressionSummaryResponse>(res);
}
// Admin Requests API
export async function fetchAdminRequests(): Promise<AdminRequestsResponse> {
  const res = await fetch(`${API_URL}/admin/requests`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch admin requests');
  return parseJson<AdminRequestsResponse>(res);
}

export async function approveAdminRequest(requestId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/admin/requests/${requestId}/approve`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to approve request');
  return parseJson<ApiData>(res);
}

export async function rejectAdminRequest(requestId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/admin/requests/${requestId}/reject`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to reject request');
  return parseJson<ApiData>(res);
}

export async function submitRequest(type: any, details: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/admin/requests/submit`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, details }),
  });
  if (!res.ok) throw new Error('Failed to submit request');
  return parseJson<ApiData>(res);
}
// Upload a roster file to backend for class/student creation
export async function uploadRoster(file: any): Promise<RosterUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(`${API_URL}/api/roster/upload`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Roster upload failed');
  }
  return parseJson<RosterUploadResponse>(res);
}
// Helper to get token from localStorage
function getToken() {
  return localStorage.getItem("ae_token");
}

// Helper to add Authorization header
function authHeaders(headers = {}) {
  const token = getToken();
  return token
    ? { ...headers, Authorization: `Bearer ${token}` }
    : headers;
}

/**
 * Drop-in replacement for fetch() that:
 *  - Automatically attaches the Bearer token
 *  - On 401 response clears localStorage and fires 'auth:logout' so the app can redirect
 */
export async function authFetch(url: string, options: RequestInit & { headers?: any } = {}) {
  const merged = {
    ...options,
    headers: {
      ...authHeaders(options.headers || {}),
    },
  };
  const res = await fetch(url, merged);
  if (res.status === 401) {
    localStorage.removeItem('ae_token');
    localStorage.removeItem('ae_user');
    window.dispatchEvent(new Event('auth:logout'));
  }
  return res;
}

// Student progress chat (Teacher/Student/Parent Connect)
export async function fetchMessagingSummary(): Promise<MessagingSummaryResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/summary`);
  if (!res.ok) throw new Error('Failed to fetch messaging summary');
  return parseJson<MessagingSummaryResponse>(res);
}

export async function fetchProgressChatContacts(): Promise<ProgressChatContactsResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/chat/contacts`);
  if (!res.ok) throw new Error('Failed to fetch chat contacts');
  return parseJson<ProgressChatContactsResponse>(res);
}

export async function fetchAdminSchoolThreads(): Promise<AdminSchoolThreadsResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/admin/school-threads`);
  if (!res.ok) throw new Error('Failed to fetch school chat threads');
  return parseJson<AdminSchoolThreadsResponse>(res);
}

export async function fetchAdminStaffContacts(): Promise<AdminStaffContactsResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/admin/staff-contacts`);
  if (!res.ok) throw new Error('Failed to fetch staff contacts');
  return parseJson<AdminStaffContactsResponse>(res);
}

export async function fetchAdminStaffThread(staffUserId: any): Promise<AdminStaffThreadResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/admin/staff-thread?staff_user_id=${staffUserId}`);
  if (!res.ok) throw new Error('Failed to fetch staff thread');
  return parseJson<AdminStaffThreadResponse>(res);
}

export async function sendAdminStaffMessage(staffUserId: any, body: any): Promise<AdminStaffMessageResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/admin/staff-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staff_user_id: staffUserId, body }),
  });
  if (!res.ok) throw new Error('Failed to send message');
  return parseJson<AdminStaffMessageResponse>(res);
}

export async function fetchProgressChatMessages(participantId: any, studentId: any): Promise<ProgressChatMessagesResponse> {
  const params = new URLSearchParams({
    participant_id: String(participantId),
    student_id: String(studentId),
  });
  const res = await authFetch(`${API_URL}/api/v1/messaging/chat/messages?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch chat messages');
  return parseJson<ProgressChatMessagesResponse>(res);
}

export async function markMessagesRead(messageIds: any): Promise<MarkMessagesReadResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_ids: messageIds }),
  });
  if (!res.ok) throw new Error('Could not save message read status. Reopen the conversation to retry.');
  return parseJson<MarkMessagesReadResponse>(res);
}

export async function sendProgressChatMessage({ participantId, studentId, body }: any): Promise<SendProgressChatMessageResponse> {
  const res = await authFetch(`${API_URL}/api/v1/messaging/chat/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      participant_id: participantId,
      student_id: studentId,
      body,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to send chat message');
  }
  return parseJson<SendProgressChatMessageResponse>(res);
}

// Upload a student profile image and persist it to that student's profile.
export async function uploadProfileImage(studentId: any, file: any): Promise<ApiData> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(`${API_URL}/api/student-profiles/${studentId}/avatar`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Upload failed (${res.status}).`);
  }
  return parseJson<ApiData>(res);
}

// Fetch a profile image from the backend (which retrieves it from S3)
export function normalizeProfileImageFilename(value: any) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const url = new URL(raw);
    return decodeURIComponent(url.pathname.replace(/^\/+/, ''));
  } catch (_err: any) {
    return raw;
  }
}

export async function fetchProfileImage(filename: any): Promise<Blob> {
  const normalizedFilename = normalizeProfileImageFilename(filename);
  const res = await authFetch(`${API_URL}/api/student/profile-image?filename=${encodeURIComponent(normalizedFilename)}`);
  if (!res.ok) throw new Error('Image fetch failed');
  return res.blob();
}

// Fetch an evidence file attached to a data point (streamed through the backend
// since the S3 bucket has ACLs disabled and the object is not publicly reachable).
export async function fetchEvidenceFile(filename: any, dataPointId: any): Promise<Blob> {
  const dataPointQuery = dataPointId != null ? `&data_point_id=${encodeURIComponent(dataPointId)}` : '';
  const res = await fetch(`${API_URL}/api/data-points/evidence/view?filename=${encodeURIComponent(filename)}${dataPointQuery}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load evidence file');
  }
  return res.blob();
}

// Fetch a student's work gallery (published task files, submissions, evidence).
export async function fetchStudentWorkGallery(studentId: any) {
  const path = studentId === 'me' ? 'me' : `students/${encodeURIComponent(studentId)}`;
  const res = await authFetch(`${API_URL}/api/work-gallery/${path}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load work gallery');
  }
  return parseJson<any>(res);
}

// Fetch a work gallery item's file through the existing authorized endpoints.
export async function fetchWorkGalleryFile(file: any): Promise<Blob> {
  if (file?.kind === 'evidence') {
    return fetchEvidenceFile(file.key, file.data_point_id);
  }
  const params = new URLSearchParams({ key: file?.key || '' });
  if (file?.task_id) params.set('task_id', file.task_id);
  const res = await authFetch(`${API_URL}/api/learning-tasks/files?${params}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load file');
  }
  return res.blob();
}

// Fetch grades from the backend
export async function fetchGrades(): Promise<GradesResponse> {
  const res = await fetch(`${API_URL}/api/grades`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Grades fetch failed');
  return parseJson<GradesResponse>(res);
}

// Submit feedback to the backend
export async function submitFeedback(feedback: any): Promise<ApiSuccessMessage> {
  const res = await fetch(`${API_URL}/api/feedback`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(feedback),
  });
  if (!res.ok) throw new Error('Feedback submit failed');
  return parseJson<ApiSuccessMessage>(res);
}

// Add API helpers for authentication and user management
export async function login(email: string, password: string, otp: string | null = null): Promise<LoginResult> {
  const res = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, otp }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Login failed');
  }
  return parseJson<LoginResult>(res);
}

export async function requestPasswordReset(email: string): Promise<ForgotPasswordResponse> {
  const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not request a password reset');
  }
  return parseJson<ForgotPasswordResponse>(res);
}

export async function resetPassword(token: any, newPassword: any): Promise<ResetPasswordResponse> {
  const res = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not reset password');
  }
  return parseJson<ResetPasswordResponse>(res);
}

export async function fetchTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const res = await authFetch(`${API_URL}/api/auth/2fa/status`, { method: 'GET' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to get 2FA status');
  }
  return parseJson<TwoFactorStatusResponse>(res);
}

export async function createTwoFactorSetup(): Promise<TwoFactorSetupResponse> {
  const res = await authFetch(`${API_URL}/api/auth/2fa/setup`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create 2FA setup');
  }
  return parseJson<TwoFactorSetupResponse>(res);
}

export async function enableTwoFactor(secret: any, otp: any): Promise<TwoFactorToggleResponse> {
  const res = await authFetch(`${API_URL}/api/auth/2fa/enable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ secret, otp }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to enable 2FA');
  }
  return parseJson<TwoFactorToggleResponse>(res);
}

export async function disableTwoFactor(otp: any): Promise<TwoFactorToggleResponse> {
  const res = await authFetch(`${API_URL}/api/auth/2fa/disable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ otp }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to disable 2FA');
  }
  return parseJson<TwoFactorToggleResponse>(res);
}

export async function changePassword(currentPassword: any, newPassword: any): Promise<ChangePasswordResponse> {
  const res = await authFetch(`${API_URL}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Password change failed');
  }
  return parseJson<ChangePasswordResponse>(res);
}

export async function fetchCurrentAuthSession(): Promise<AuthLoginResponse> {
  const res = await authFetch(`${API_URL}/api/auth/me`, {
    method: 'GET',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to refresh auth session');
  }
  return parseJson<AuthLoginResponse>(res);
}

export async function rotateBootstrapPassword(email: string, currentPassword: string, newPassword: string): Promise<RotateBootstrapPasswordResponse> {
  const res = await fetch(`${API_URL}/api/auth/bootstrap-rotate-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Password rotation failed');
  }
  return parseJson<RotateBootstrapPasswordResponse>(res);
}

export async function register(user: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(user),
  });
  if (!res.ok) throw new Error('Registration failed');
  return parseJson<ApiData>(res);
}

export async function fetchStudents(): Promise<TeacherStudentsResponse> {
  const res = await fetch(`${API_URL}/api/teacher/students`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch students failed');
  return parseJson<TeacherStudentsResponse>(res);
}

export async function fetchTeacherStudentDetail(studentId: any): Promise<TeacherStudentDetailResponse> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch teacher student detail failed');
  return parseJson<TeacherStudentDetailResponse>(res);
}

async function readApiError(res, fallbackMessage) {
  const err = await res.json().catch(() => ({}));
  throw new Error(err.detail || fallbackMessage);
}

export async function fetchTeacherStudentParents(studentId: any): Promise<TeacherParentsResponse> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}/parents`, {
    headers: authHeaders(),
  });
  if (!res.ok) await readApiError(res, 'Failed to fetch parent associations');
  return parseJson<TeacherParentsResponse>(res);
}

export async function addTeacherStudentParent(studentId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}/parents`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readApiError(res, 'Failed to link parent to student');
  return parseJson<ApiData>(res);
}

export async function updateTeacherStudentParent(studentId: any, parentId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}/parents/${parentId}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readApiError(res, 'Failed to update parent association');
  return parseJson<ApiData>(res);
}

export async function removeTeacherStudentParent(studentId: any, parentId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}/parents/${parentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await readApiError(res, 'Failed to remove parent association');
  return parseJson<ApiData>(res);
}

export async function fetchAdminStudentParents(studentId: any): Promise<TeacherParentsResponse> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}/parents`, {
    headers: authHeaders(),
  });
  if (!res.ok) await readApiError(res, 'Failed to fetch parent associations');
  return parseJson<TeacherParentsResponse>(res);
}

export async function addAdminStudentParent(studentId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}/parents`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readApiError(res, 'Failed to link parent to student');
  return parseJson<ApiData>(res);
}

export async function updateAdminStudentParent(studentId: any, parentId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}/parents/${parentId}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readApiError(res, 'Failed to update parent association');
  return parseJson<ApiData>(res);
}

export async function removeAdminStudentParent(studentId: any, parentId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}/parents/${parentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await readApiError(res, 'Failed to remove parent association');
  return parseJson<ApiData>(res);
}

export async function fetchParentStudents(): Promise<ParentStudentsResponse> {
  const res = await authFetch(`${API_URL}/api/parent/students`);
  if (!res.ok) throw new Error('Failed to fetch parent students');
  return parseJson<ParentStudentsResponse>(res);
}

export async function fetchParentRightsRequests(studentId: any): Promise<ParentRightsRequestsResponse> {
  const params = new URLSearchParams();
  if (studentId) params.set('student_id', String(studentId));
  const qs = params.toString();
  const res = await authFetch(`${API_URL}/api/parent/rights-requests${qs ? `?${qs}` : ''}`);
  if (!res.ok) await readApiError(res, 'Failed to load rights requests');
  return parseJson<ParentRightsRequestsResponse>(res);
}

export async function createParentRightsRequest({ studentId, requestType, requestDetail }: any): Promise<ParentRightsRequestMutationResponse> {
  const res = await authFetch(`${API_URL}/api/parent/rights-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      student_id: studentId,
      request_type: requestType,
      request_detail: requestDetail || null,
    }),
  });
  if (!res.ok) await readApiError(res, 'Failed to submit rights request');
  return parseJson<ParentRightsRequestMutationResponse>(res);
}

export async function listSchoolRightsRequests(schoolId: any, { studentId, status = '', openOnly = false, limit = 25, page = 1 }: any = {}): Promise<RightsRequestsResponse> {
  if (!schoolId) throw new Error('schoolId is required');
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
  });
  if (studentId) params.set('student_id', String(studentId));
  if (status) params.set('status', status);
  if (openOnly) params.set('open_only', 'true');
  const res = await fetch(
    `${API_URL}/api/schools/${schoolId}/rights-requests?${params.toString()}`,
    { headers: authHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error: any = new Error(err.detail || 'Failed to load rights requests');
    error.status = res.status;
    throw error;
  }
  return parseJson<RightsRequestsResponse>(res);
}

export async function updateSchoolRightsRequest(schoolId: any, requestId: any, payload: any): Promise<ApiData> {
  if (!schoolId || !requestId) throw new Error('schoolId and requestId are required');
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/rights-requests/${requestId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error: any = new Error(err.detail || 'Failed to update rights request');
    error.status = res.status;
    throw error;
  }
  return parseJson<ApiData>(res);
}

export async function fetchParentInvitationRecipients(): Promise<ParentInvitationRecipientsResponse> {
  const res = await authFetch(`${API_URL}/api/admin/parent-invitations`);
  if (!res.ok) await readApiError(res, 'Failed to load parent invitations');
  return parseJson<ParentInvitationRecipientsResponse>(res);
}

export async function fetchManagedAccounts(): Promise<ManagedAccountsResponse> {
  const res = await authFetch(`${API_URL}/api/admin/user-management`);
  if (!res.ok) await readApiError(res, 'Failed to load accounts');
  return parseJson<ManagedAccountsResponse>(res);
}

export async function fetchAccountHistory(userId: any): Promise<AccountHistoryResponse> {
  const res = await authFetch(`${API_URL}/api/admin/user-management/${userId}/history`);
  if (!res.ok) await readApiError(res, 'Failed to load account history');
  return parseJson<AccountHistoryResponse>(res);
}

export async function setAccountAccess(userId: any, enabled: any): Promise<AccountAccessResponse> {
  const res = await authFetch(`${API_URL}/api/admin/user-management/${userId}/access`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
  });
  if (!res.ok) await readApiError(res, 'Failed to change account access');
  return parseJson<AccountAccessResponse>(res);
}

export async function sendManagedAccountEmail(userId: any, kind: any, resend: any = false): Promise<AccountEmailResponse> {
  const res = await authFetch(`${API_URL}/api/admin/user-management/${userId}/email`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, resend }),
  });
  if (!res.ok) await readApiError(res, 'Failed to send account email');
  return parseJson<AccountEmailResponse>(res);
}

export async function sendParentInvitations(userIds: any, resend: any = false): Promise<ParentInvitationsSendResponse> {
  const res = await authFetch(`${API_URL}/api/admin/parent-invitations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, resend }),
  });
  if (!res.ok) await readApiError(res, 'Failed to send parent invitations');
  return parseJson<ParentInvitationsSendResponse>(res);
}

export async function updateTeacherStudentDetail(studentId: any, data: any): Promise<TeacherStudentDetailResponse> {
  const res = await fetch(`${API_URL}/api/teacher/students/${studentId}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update teacher student detail failed');
  return parseJson<TeacherStudentDetailResponse>(res);
}

export async function fetchTeacherClassDetail(classId: any): Promise<TeacherClassDetailResponse> {
  const res = await authFetch(`${API_URL}/api/teacher/classes/${classId}`);
  if (!res.ok) throw new Error('Failed to fetch class detail');
  return parseJson<TeacherClassDetailResponse>(res);
}

export async function renameTeacherClass(classId: any, className: any): Promise<TeacherClassDetailResponse> {
  const res = await authFetch(`${API_URL}/api/teacher/classes/${classId}/name`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ class_name: className }),
  });
  if (!res.ok) throw new Error('Failed to rename class');
  return parseJson<TeacherClassDetailResponse>(res);
}

export async function updateTeacherClassStudents(classId: any, { add_student_ids = [], remove_student_ids = [] }: any = {}): Promise<TeacherClassDetailResponse> {
  const res = await authFetch(`${API_URL}/api/teacher/classes/${classId}/students`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ add_student_ids, remove_student_ids }),
  });
  if (!res.ok) throw new Error('Failed to update class students');
  return parseJson<TeacherClassDetailResponse>(res);
}

export async function moveStudentBetweenClasses(fromClassId: any, studentId: any, toClassId: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/teacher/classes/${fromClassId}/move-student`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ student_id: studentId, to_class_id: toClassId }),
  });
  if (!res.ok) throw new Error('Failed to move student');
  return parseJson<ApiData>(res);
}

export async function fetchTeachers(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/teachers`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch teachers failed');
  return parseJson<ApiData>(res);
}

export async function fetchAdminStudents(): Promise<AdminStudentsResponse> {
  const res = await fetch(`${API_URL}/api/admin/students`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch admin students failed');
  return parseJson<AdminStudentsResponse>(res);
}

export async function createAdminStudent(data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.detail || 'Create admin student failed');
  }
  return parseJson<ApiData>(res);
}

export async function fetchAdminStudentDetail(studentId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch admin student detail failed');
  return parseJson<ApiData>(res);
}

export async function updateAdminStudentDetail(studentId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update admin student detail failed');
  return parseJson<ApiData>(res);
}

export async function fetchIreadyScores(studentId: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/students/${studentId}/iready-scores`);
  if (!res.ok) throw new Error('Failed to fetch i-Ready scores');
  return parseJson<ApiData>(res);
}

export async function saveIreadyScore(studentId: any, { subject, window, score, school_year }: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/students/${studentId}/iready-scores`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, window, score, school_year }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to save i-Ready score');
  }
  return parseJson<ApiData>(res);
}

export async function deleteAdminStudent(studentId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/${studentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Delete admin student failed');
  return parseJson<ApiData>(res);
}

export async function cleanupDeletedStudentEnrollments(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/students/cleanup-deleted-enrollments`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Cleanup deleted student enrollments failed');
  return parseJson<ApiData>(res);
}

export async function fetchAdminTeachers(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/teachers`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch admin teachers failed');
  return parseJson<ApiData>(res);
}

export async function fetchAdminTeacherDetail(teacherId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/teachers/${teacherId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Fetch admin teacher detail failed');
  return parseJson<ApiData>(res);
}

export async function updateAdminTeacher(teacherId: any, data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/admin/teachers/${teacherId}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update admin teacher failed');
  return parseJson<ApiData>(res);
}

export async function fetchClasses(): Promise<ApiData> {
  const rawUser = localStorage.getItem('ae_user');
  const role = rawUser ? JSON.parse(rawUser)?.role : undefined;

  const endpoint = (role === 'teacher' || role === 'special_services_teacher')
    ? `${API_URL}/classes`
    : `${API_URL}/api/classes`;

  const res = await fetch(endpoint, {
    headers: authHeaders(),
  });

  if (res.ok) return parseJson<ApiData>(res);

  // Student and other non-admin/non-teacher roles may not have classes endpoint access.
  if (res.status === 403 || res.status === 404) return [];

  throw new Error('Fetch classes failed');
}

export async function updateUserInfo({ email, phone }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/user/update`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ email, phone }),
  });
  if (!res.ok) throw new Error("Failed to update user info");
  return parseJson<ApiData>(res);
}

// Get staged rows for a roster upload
export async function getStagedRows(uploadId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/roster/staged/${uploadId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch staged rows');
  return parseJson<ApiData>(res);
}

// Get staged rows with errors for a roster upload
export async function getStagedErrors(uploadId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/roster/errors/${uploadId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch error rows');
  return parseJson<ApiData>(res);
}

// Get roster upload status
export async function getRosterUploadStatus(uploadId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/roster/status/${uploadId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch upload status');
  return parseJson<ApiData>(res);
}

// Reprocess failed staged rows for a roster upload
export async function reprocessFailedRows(uploadId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/roster/reprocess/${uploadId}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to reprocess failed rows');
  return parseJson<ApiData>(res);
}

// List all roster uploads for a school
export async function listRosterUploads(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/roster/uploads/${schoolId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch uploads');
  return parseJson<ApiData>(res);
}

// Process staged rows for a roster upload
export async function processStagedRows(uploadId: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/roster/process/${uploadId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to process staged rows');
  return parseJson<ApiData>(res);
}

// ── School management (superadmin) ──────────────────────────────────────────

export async function listSchools(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch schools');
  return parseJson<ApiData>(res);
}

export async function createSchool({ name, address, phone, state }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, address, phone, state }),
  });
  if (!res.ok) throw new Error('Failed to create school');
  return parseJson<ApiData>(res);
}

export async function createDistrictOnboarding(payload: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/districts/onboard`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : detail?.message || (Array.isArray(detail) ? detail.map((d: any) => d.msg || d).join('; ') : null);
    throw new Error(message || 'Failed to create district');
  }
  return parseJson<ApiData>(res);
}

export async function listBillingDistricts(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/districts`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to list districts');
  return parseJson<ApiData>(res);
}

export async function resendDistrictInvite(districtId: number, userId: number): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/districts/${districtId}/resend-invite`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw new Error('Failed to resend invite');
  return parseJson<ApiData>(res);
}

export async function forceDistrictBillingStatus(
  districtId: number,
  billingStatus: string,
  paymentRequired?: boolean,
): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/districts/${districtId}/force-billing-status`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ billing_status: billingStatus, payment_required: paymentRequired }),
  });
  if (!res.ok) throw new Error('Failed to update billing status');
  return parseJson<ApiData>(res);
}

export async function getBillingStatus(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/billing/status`);
  if (!res.ok) throw new Error('Failed to load billing status');
  return parseJson<ApiData>(res);
}

export async function createBillingCheckoutSession(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/billing/checkout-session`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to start Checkout');
  }
  return parseJson<ApiData>(res);
}

export async function createBillingPortalSession(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/billing/portal-session`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to open billing portal');
  }
  return parseJson<ApiData>(res);
}

export async function verifyBillingCheckoutSession(sessionId: string): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/billing/checkout-session/${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error('Failed to verify Checkout session');
  return parseJson<ApiData>(res);
}

export async function updateSchool(schoolId: any, { name, address, phone, state }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, address, phone, state }),
  });
  if (!res.ok) throw new Error('Failed to update school');
  return parseJson<ApiData>(res);
}

export async function fetchSchoolDisplaySettings(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/display-settings`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch display settings');
  return parseJson<ApiData>(res);
}

export async function updateSchoolDisplaySettings(schoolId: any, settings: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/display-settings`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error('Failed to update display settings');
  return parseJson<ApiData>(res);
}

export async function deleteSchool(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete school');
  }
  return parseJson<ApiData>(res);
}

export async function getSchoolDeleteCheck(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/delete-check`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to check school delete constraints');
  }
  return parseJson<ApiData>(res);
}

export async function provisionSchoolUser(schoolId: any, { email, password, first_name, last_name, role, send_invite }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/provision-user`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, first_name, last_name, role, send_invite }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to provision user');
  }
  return parseJson<ApiData>(res);
}

export async function grantAdminAccess(schoolId: any, { email, access_level = 'school_admin' }: any = {}): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/grant-admin`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, access_level }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to grant admin access');
  }
  return parseJson<ApiData>(res);
}

export async function listSchoolAdmins(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admins`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to list school admins');
  }
  return parseJson<ApiData>(res);
}

export async function listSchoolTeachers(schoolId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/teachers`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to list school teachers');
  }
  return parseJson<ApiData>(res);
}

export async function resetSchoolTeacherPassword(schoolId: any, userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/teachers/${userId}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to reset teacher password');
  }
  return parseJson<ApiData>(res);
}

export async function deleteSchoolTeacher(schoolId: any, userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/teachers/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete teacher');
  }
  return parseJson<ApiData>(res);
}

// ── Standards visibility scope (superadmin config + per-user view preference) ──

export async function listStandardsScopeStates(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/states`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load states');
  return parseJson<ApiData>(res);
}

export async function getStandardsScopeRulesSummary(state: any): Promise<ApiData> {
  const params = new URLSearchParams({ state });
  const res = await fetch(`${API_URL}/api/standards-scope/rules/summary?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load standards scope summary');
  return parseJson<ApiData>(res);
}

export async function getStandardsScopeRules({ state, subject, gradeBand, timeFrameType, timeFrameLabel }: any): Promise<ApiData> {
  const params = new URLSearchParams({
    state,
    subject,
    grade_band: gradeBand,
    time_frame_type: timeFrameType,
    time_frame_label: timeFrameLabel,
  });
  const res = await fetch(`${API_URL}/api/standards-scope/rules?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load standards scope rules');
  return parseJson<ApiData>(res);
}

export async function saveStandardsScopeRules({ state, subject, gradeBand, timeFrameType, timeFrameLabel, mlsCodes }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/rules`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      state,
      subject,
      grade_band: gradeBand,
      time_frame_type: timeFrameType,
      time_frame_label: timeFrameLabel,
      mls_codes: mlsCodes,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save standards scope rules');
  }
  return parseJson<ApiData>(res);
}

export async function getDistrictStandardsScope(districtId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/districts?district_id=${encodeURIComponent(districtId)}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load district standards preferences');
  return parseJson<ApiData>(res);
}

export async function saveDistrictStandardsScope({ districtId, subject, standardSource, gradeBand, timeFrameType, timeFrameLabel, mlsCodes }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/districts/${encodeURIComponent(districtId)}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject,
      standard_source: standardSource,
      grade_band: gradeBand,
      time_frame_type: timeFrameType,
      time_frame_label: timeFrameLabel,
      mls_codes: mlsCodes,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save district standards preference');
  }
  return parseJson<ApiData>(res);
}

export async function getMyStandardsViewPreference(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/my-preference`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load view preference');
  return parseJson<ApiData>(res);
}

export async function saveMyStandardsViewPreference({ viewMode, timeFrameType, timeFrameLabel }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/my-preference`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ view_mode: viewMode, time_frame_type: timeFrameType, time_frame_label: timeFrameLabel }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save view preference');
  }
  return parseJson<ApiData>(res);
}

export async function listStandardsScopeTimeFrames({ subject, gradeBand }: any): Promise<ApiData> {
  const params = new URLSearchParams({ subject, grade_band: gradeBand });
  const res = await fetch(`${API_URL}/api/standards-scope/time-frames?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load time frames');
  return parseJson<ApiData>(res);
}

export async function getEffectiveStandardsVisibility({ subject, gradeBand }: any): Promise<ApiData> {
  const params = new URLSearchParams({ subject, grade_band: gradeBand });
  const res = await fetch(`${API_URL}/api/standards-scope/effective?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load effective standards visibility');
  return parseJson<ApiData>(res);
}

export async function listCustomStandardSets({ subject, gradeBand }: any = {}): Promise<ApiData> {
  const params = new URLSearchParams();
  if (subject) params.append('subject', subject);
  if (gradeBand) params.append('grade_band', gradeBand);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/api/standards-scope/custom-sets${query}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to list custom standard sets');
  return parseJson<ApiData>(res);
}

export async function createCustomStandardSet({ subject, gradeBand, timeFrameType, timeFrameLabel, mlsCodes }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/custom-sets`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject,
      grade_band: gradeBand,
      time_frame_type: timeFrameType,
      time_frame_label: timeFrameLabel,
      mls_codes: mlsCodes || [],
    }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.detail || 'Failed to create custom standard set');
  }
  return parseJson<ApiData>(res);
}

export async function updateCustomStandardSet(setId: any, { timeFrameType, timeFrameLabel, mlsCodes }: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/custom-sets/${setId}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      time_frame_type: timeFrameType,
      time_frame_label: timeFrameLabel,
      mls_codes: mlsCodes,
    }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.detail || 'Failed to update custom standard set');
  }
  return parseJson<ApiData>(res);
}

export async function deleteCustomStandardSet(setId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/standards-scope/custom-sets/${setId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete custom standard set');
  return parseJson<ApiData>(res);
}

export async function fetchStandardsProgressionData(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/progression/standards-progression-data`);
  if (!res.ok) throw new Error('Failed to load standards progression data');
  return parseJson<ApiData>(res);
}



export async function revokeSchoolAdminAccess(schoolId: any, userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admins/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to revoke admin access');
  }
  return parseJson<ApiData>(res);
}

export async function updateSchoolAdminAccessLevel(schoolId: any, userId: any, access_level: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admins/${userId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_level }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to update admin access level');
  }
  return parseJson<ApiData>(res);
}

export async function resetSchoolAdminPassword(schoolId: any, userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admins/${userId}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to reset admin password');
  }
  return parseJson<ApiData>(res);
}

export async function deleteSchoolAdminAccount(schoolId: any, userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admins/${userId}/account`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete admin account');
  }
  return parseJson<ApiData>(res);
}

export async function listDemoUsers(): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/demo-users`, { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load demo users');
  }
  return parseJson<ApiData>(res);
}

export async function createDemoUser(data: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/demo-users`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create demo user');
  }
  return parseJson<ApiData>(res);
}

export async function resetDemoUser(userId: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/demo-users/${userId}/reset`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to reset demo user');
  }
  return parseJson<ApiData>(res);
}

export async function listSchoolAdminAudit(schoolId: any, { limit = 25, page = 1, actionPrefix = '', startDate = '', endDate = '' }: any = {}): Promise<ApiData> {
  const params = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (actionPrefix) params.set('action_prefix', actionPrefix);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);

  const res = await fetch(`${API_URL}/api/schools/${schoolId}/admin-audit?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load admin audit');
  }
  return parseJson<ApiData>(res);
}

export async function listStudentDisclosureRecords(schoolId: any, {
    studentId,
    limit = 25,
    page = 1,
    action = '',
    startDate = '',
    endDate = '',
  }: any = {}): Promise<ApiData> {
  if (!schoolId || !studentId) {
    throw new Error('schoolId and studentId are required');
  }
  const params = new URLSearchParams({
    student_id: String(studentId),
    limit: String(limit),
    page: String(page),
  });
  if (action) params.set('action', action);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);

  const res = await fetch(
    `${API_URL}/api/schools/${schoolId}/disclosure-records?${params.toString()}`,
    { headers: authHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error: any = new Error(err.detail || 'Failed to load disclosure records');
    error.status = res.status;
    throw error;
  }
  return parseJson<ApiData>(res);
}

export async function submitContactForm(contactData: any): Promise<ApiData> {
  const res = await fetch(`${API_URL}/api/contact/submit`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(contactData),
  });
  if (!res.ok) throw new Error('Contact form submission failed');
  return parseJson<ApiData>(res);
}


// Batched so the query string stays well under server URL limits (thousands of codes once every subject is loaded).
export const PROGRESS_SUMMARY_BATCH_SIZE = 150;

export async function fetchProgressSummaryByStandard(standardCodes: any = []): Promise<ApiData> {
  const codes = Array.from(new Set((standardCodes || []).filter(Boolean)));
  const batches = [];
  for (let index = 0; index < codes.length; index += PROGRESS_SUMMARY_BATCH_SIZE) {
    batches.push(codes.slice(index, index + PROGRESS_SUMMARY_BATCH_SIZE));
  }
  const responses = await Promise.all(batches.map(async (batch) => {
    const query = new URLSearchParams();
    batch.forEach((code) => query.append('standard_codes', code));
    const res = await authFetch(`${API_URL}/api/data-points/progress/summary-by-standard?${query.toString()}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const error: any = new Error('Failed to load data point progress');
      error.status = res.status;
      throw error;
    }
    return parseJson<ApiData[]>(res);
  }));
  const lookup: ApiData = {};
  responses.flat().forEach((item) => {
    if (item?.standard_code) lookup[item.standard_code] = item;
  });
  return lookup;
}

// Read-only family data points; the backend enforces child links and shared content.
export async function fetchFamilyDataPoints(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/parent/data-points`);
  if (!res.ok) throw new Error("Could not load your children's data points.");
  return parseJson<ApiData>(res);
}

// Fetch all data points report, optionally filtered by student_id or class_id
export async function fetchDataPointsReport({ studentId, classId }: any = {}): Promise<ApiData> {
  const params = new URLSearchParams();
  if (studentId) params.set('student_id', studentId);
  if (classId) params.set('class_id', classId);
  const res = await authFetch(`${API_URL}/api/data-points/report?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch data points report');
  return parseJson<ApiData>(res);
}

export async function fetchDataPointsReportClasses(): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/data-points/report/classes`);
  if (!res.ok) throw new Error('Failed to load classes');
  return parseJson<ApiData>(res);
}

export async function downloadDataPointsTranscriptPdf({ ids, filters }: any): Promise<Blob> {
  const res = await authFetch(`${API_URL}/api/data-points/report/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, filters }),
  });
  if (!res.ok) throw new Error('Failed to download the PDF transcript');
  return res.blob();
}

export async function updateDataPoint(dataPointId: any, payload: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/data-points/${dataPointId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to update data point');
  }
  return parseJson<ApiData>(res);
}

export async function deleteDataPoint(dataPointId: any): Promise<ApiData> {
  const res = await authFetch(`${API_URL}/api/data-points/${dataPointId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete data point');
  }
  return parseJson<ApiData>(res);
}
